import React, { useState, useEffect } from 'react';
import Image from 'gatsby-plugin-sanity-image';
import { Link } from 'gatsby';
import { motion, AnimatePresence } from 'framer-motion';
import cx from 'classnames';

import { GetBlogs } from '@querys/getBlogs';

import { BlockWrapper } from '../blockWrapper';
import { Arrow } from '@svg/arrow';

const News = ({ heading, blockConfig }) => {
  const blogs = GetBlogs().slice(0, 4);
  const [activeBlog, setActiveBlog] = useState(blogs[0]);

  const [hover, setHover] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.innerWidth < 1024 && setIsMobile(true);
  }, []);

  let arrowVars = {};

  if (!isMobile) {
    arrowVars = {
      initial: {
        x: -20,
        transition: {
          duration: 0.3,
        },
      },
      animate: {
        x: 0,
        transition: {
          duration: 0.3,
        },
      },
    };
  }

  return (
    <BlockWrapper className={`max-w-none grid grid-cols-14`} {...blockConfig}>
      <h2 className='col-start-2 col-end-14 text-3xl leading-3xl md:text-5xl md:leading-5xl font-medium'>
        {heading}
      </h2>

      {/* Desktop blog switch */}
      <div className='hidden lg:flex col-start-2 col-end-14 flex-row mt-12'>
        {blogs.map((blog, i) => {
          const { category, title } = blog;
          return (
            <div
              key={i}
              onClick={() => setActiveBlog(blogs[i])}
              className={cx(
                'cursor-pointer w-full mr-auto flex justify-center flex-col pr-4',
                {
                  'border-l pl-4': i !== 0,
                  'opacity-50': blog !== activeBlog,
                },
              )}>
              <p className='text-base leading-base text-grey'>{category}</p>
              <p className='text-xl leading-xl font-medium'>{title}</p>
            </div>
          );
        })}
      </div>

      {/* Mobile Link to blog */}
      <div className='lg:hidden col-start-2 col-end-14 flex flex-col mt-10'>
        {blogs.map((blog, i) => {
          const { category, title, date, excerpt } = blog;
          return (
            <div
              key={i}
              onClick={() => setActiveBlog(blogs[i])}
              className={cx(
                'cursor-pointer w-full flex justify-center flex-col',
                {
                  'mt-10': i !== 0,
                },
              )}>
              <p className='text-sm leading-sm text-grey font-medium'>
                {category} - {date}
              </p>
              <p className='text-2xl leading-2xl mt-3'>{title}</p>
              <p className='text-sm leading-sm pt-3 mt-3 border-t border-grey'>
                {excerpt}
              </p>
              <Link
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                to={`/news/${activeBlog.slug.current}`}
                className='text-sm leading-sm font-semibold mt-4 flex flex-row justify-between items-center'>
                <span>Read article</span> <Arrow white={false} />
              </Link>
            </div>
          );
        })}
      </div>

      {/* Desktop Selected Blog */}
      <style>
        {`\
                .news-rollover:hover{\
                    background-color: ${activeBlog.blogColor.hex}\
                }\
                .news-rollover:hover .rollover-category{\
                    color: #000 !important;\
                }\
            `}
      </style>

      <AnimatePresence exitBeforeEnter>
        <motion.div
          key={activeBlog?.title}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className='hidden lg:grid news-rollover transition duration-300 ease-in-out col-start-1 col-end-15 mt-12 grid-cols-14 items-center justify-center'>
          <div className='w-full my-12 col-start-2 col-end-14 lg:col-end-8 overflow-hidden aspect-w-2 aspect-h-2'>
            {activeBlog?.external ? (
              <a
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                href={activeBlog?.external}
                target='_blank'
                rel='noreferrer'>
                <Image
                  className='transform hover:scale-105 transition duration-300 ease-in-out absolute top-0 left-0 object-cover w-full h-full'
                  {...activeBlog?.featuredImage?.image}
                />
              </a>
            ) : (
              <Link
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                to={`/news/${activeBlog?.slug?.current}`}>
                <Image
                  className='transform hover:scale-105 transition duration-300 ease-in-out absolute top-0 left-0 object-cover w-full h-full'
                  {...activeBlog?.featuredImage?.image}
                />
              </Link>
            )}
          </div>

          <div className='w-full col-start-2 lg:col-start-8 col-end-14 pl-16'>
            <p className='text-xl leading-xl text-grey rollover-category transition duration-300 ease-in-out'>
              {activeBlog.category} - {activeBlog.date}
            </p>
            <h3 className='text-5xl leading-5xl font-medium mt-6'>
              {activeBlog.title}
            </h3>
            <p className='text-base leading-base pt-12 mt-16 border-t border-grey md:text-lg md:leading-lg'>
              {activeBlog.excerpt}
            </p>
            <div className='mt-4'>
              {activeBlog?.external ? (
                <a
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  href={activeBlog?.external}
                  target='_blank'
                  rel='noreferrer'
                  className='font-semibold mt-0-important text-lg leading-lg w-full flex flex-row justify-between items-center'>
                  Read article
                  <motion.span
                    variants={arrowVars}
                    initial='initial'
                    animate={hover ? 'animate' : 'initial'}>
                    <Arrow />
                  </motion.span>
                </a>
              ) : (
                <Link
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  to={`/news/${activeBlog.slug.current}`}
                  className='font-semibold mt-0-important text-lg leading-lg w-full flex flex-row justify-between items-center'>
                  Read article
                  <motion.span
                    variants={arrowVars}
                    initial='initial'
                    animate={hover ? 'animate' : 'initial'}>
                    <Arrow />
                  </motion.span>
                </Link>
              )}
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </BlockWrapper>
  );
};

export default News;
